/**
 * The written side of a conversation: what is on screen, what is queued for a
 * session that is not open yet, and what is still being typed.
 *
 * No SDK and no DOM. The controller owns the connection and tells this class
 * what happened on it; the panel renders `entries` as they are. Every method
 * replaces `entries` rather than mutating it, so a Lit property bound to it
 * sees the change.
 */

export interface TranscriptEntry {
  id: number;
  role: "user" | "ai";
  text: string;
  ts: number;
  /**
   * True for a typed message shown before its session is open.
   *
   * The visitor sees their own words the moment they press send; the chat
   * surface renders them faded until the connection exists and they leave.
   */
  pending?: boolean;
}

/**
 * Id of the seeded greeting.
 *
 * Fixed rather than drawn from the counter so a reseed re-uses the same node
 * instead of making `repeat()` rebuild it, and so the provider's own opening
 * line can replace it by id. Entry ids start at 1, leaving 0 free.
 */
const GREETING_ENTRY_ID = 0;

/** How long a provider echo of a typed message still counts as the same message. */
const ECHO_WINDOW_MS = 5_000;

export class ChatThread {
  entries: TranscriptEntry[] = [];
  /**
   * A typed message waiting for its session.
   *
   * Set when someone writes before a chat exists: the session opens, and the
   * message goes out as soon as it connects. Its bubble is on screen, faded.
   */
  pending: string | null = null;
  /** True between a typed message and the agent's next written reply. */
  awaitingReply = false;
  /** What the visitor is typing, or the words a failed start handed back. */
  draft = "";

  private nextId = 1;
  private lastTypedTurn: { text: string; ts: number } | null = null;
  /** True once the agent has written in this chat, so the greeting is settled. */
  private greetingSettled = false;

  /**
   * Start the thread over on the greeting.
   *
   * The greeting is written with the same placeholders the agent resolves at
   * runtime; showing "{{nome}}" to a visitor is worse than showing nothing,
   * and the values are already here. A queued message keeps its place after
   * the greeting: it is still waiting for the session this seed opens.
   */
  seed(greeting: string | null, context: Record<string, string>): void {
    this.greetingSettled = false;
    this.awaitingReply = false;
    this.entries =
      greeting === null
        ? []
        : [
            {
              id: GREETING_ENTRY_ID,
              role: "ai",
              text: interpolate(greeting, context),
              ts: Date.now(),
            },
          ];
    if (this.pending !== null) {
      this.entries = [...this.entries, this.turn("user", this.pending, true)];
    }
  }

  /** Show a typed message at once, faded, and hold its text until `flush()`. */
  queue(text: string): void {
    this.pending = text;
    this.entries = [...this.entries, this.turn("user", text, true)];
  }

  /**
   * Send whatever is queued, now that a session can carry it.
   *
   * The bubble settles and a reply is awaited. Returns false when there was
   * nothing to send, so the caller can skip whatever a send is worth.
   */
  flush(send: (text: string) => void): boolean {
    const text = this.pending;
    if (text === null) return false;
    this.pending = null;
    send(text);
    this.lastTypedTurn = { text, ts: Date.now() };
    this.entries = this.entries.map((entry) =>
      entry.pending ? { ...entry, pending: false } : entry,
    );
    this.awaitingReply = true;
    return true;
  }

  /**
   * Take the queued message back off screen after a session that never
   * opened. The words go into `draft`: they belong back in the composer, not
   * in a transcript of a conversation that does not exist.
   */
  unqueue(): string | null {
    const text = this.pending;
    if (text === null) return null;
    this.pending = null;
    this.entries = this.entries.filter((entry) => !entry.pending);
    this.draft = text;
    return text;
  }

  /**
   * Queue the last thing the visitor typed for a reconnect.
   *
   * A chat that dropped before the reply arrived left a message that was sent
   * to a session which then died. Its bubble is still on screen and goes back
   * to looking unsent, so the retry carries it rather than losing it.
   */
  requeueLastUserTurn(): void {
    const last = [...this.entries].reverse().find((e) => e.role === "user");
    if (!last) return;
    this.pending = last.text;
    this.entries = this.entries.map((entry) =>
      entry.id === last.id ? { ...entry, pending: true } : entry,
    );
  }

  /**
   * The agent wrote something.
   *
   * A chat opened on the seeded greeting, and the agent now says its own
   * opening line: that line takes the seeded bubble's place rather than
   * appearing under a near-copy of itself. Only once: everything after the
   * first agent turn is a reply.
   */
  acceptAgentTurn(text: string): void {
    this.awaitingReply = false;
    if (!this.greetingSettled) {
      this.greetingSettled = true;
      if (this.entries.some((e) => e.id === GREETING_ENTRY_ID)) {
        this.entries = this.entries.map((entry) =>
          entry.id === GREETING_ENTRY_ID
            ? { ...entry, text, ts: Date.now() }
            : entry,
        );
        return;
      }
    }
    this.append("ai", text);
  }

  /** A turn with no rule attached: it goes at the end, as said. */
  append(role: TranscriptEntry["role"], text: string): void {
    this.entries = [...this.entries, this.turn(role, text)];
  }

  /**
   * Treat the greeting as already answered.
   *
   * For a reconnect: the visitor is in the middle of a conversation, so the
   * agent's next line is a reply, not an opening to swap in.
   */
  settleGreeting(): void {
    this.greetingSettled = true;
  }

  /**
   * True when the provider is handing back a message the visitor typed.
   *
   * A typed message is shown the moment it is sent; if the provider echoes it
   * as a user turn shortly after, that echo is not a second message. Consumed
   * on match, so the same words typed twice still show twice.
   */
  isEchoOfTypedTurn(text: string): boolean {
    const last = this.lastTypedTurn;
    if (!last) return false;
    const isEcho =
      last.text === text.trim() && Date.now() - last.ts < ECHO_WINDOW_MS;
    if (isEcho) this.lastTypedTurn = null;
    return isEcho;
  }

  /** The session is over: nothing is queued or awaited, but what was said stays. */
  sessionClosed(): void {
    this.pending = null;
    this.awaitingReply = false;
    this.lastTypedTurn = null;
  }

  reset(): void {
    this.entries = [];
    this.pending = null;
    this.awaitingReply = false;
    this.draft = "";
    this.lastTypedTurn = null;
    this.greetingSettled = false;
  }

  private turn(
    role: TranscriptEntry["role"],
    text: string,
    pending = false,
  ): TranscriptEntry {
    const entry: TranscriptEntry = {
      id: this.nextId++,
      role,
      text,
      ts: Date.now(),
    };
    return pending ? { ...entry, pending: true } : entry;
  }
}

/**
 * Fill `{{key}}` placeholders from the context the page supplied.
 *
 * Unknown keys are left exactly as written: a placeholder nobody set is a
 * configuration mistake, and silently blanking it hides it from the operator.
 *
 * The pattern is the canonical one from
 * packages/shared-types/src/utils/dynamic-variables.ts (`dynamicVariableRegex`),
 * copied because this bundle takes no monorepo dependency. Keep them identical:
 * a placeholder the builder accepts must be one the widget fills.
 */
export function interpolate(
  text: string,
  values: Record<string, string>,
): string {
  return text.replace(
    /\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g,
    (match, key: string) => values[key] ?? match,
  );
}
